import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Stepper } from "components";
import { Box, Step, StepLabel, StepContent, Button, Typography, Container } from "@material-ui/core";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AdSizeType, BriefingFormModel } from "./interfaces";
import { briefingFormUpdateSchema, TRU_SKIN } from "validation/components/BriefingForm/briefingFormRules";
import { isEmpty } from "utils";
import { useDispatch, useSelector } from "react-redux";
import {
    removeBriefFormAsset,
    submitBriefForm,
    uploadBriefFormAsset,
    fetchBriefForm,
    uploadBriefFormAdditionalAssets,
} from "store/features/briefForm/briefForm.actions";
import {
    getBriefForm,
    getBriefFormProcessingStatus,
    hasFormBeenSubmitted,
} from "store/features/briefForm/briefForm.selectors";
import { getAd, getAdStatus } from "store/features/materialSpecs/ad/ad.selectors";
import { useProcessingStatus } from "hooks";
import { createBriefingFormUpdateModel } from "api/factories/briefingForm.factory";
import { fetchAd } from "store/features/materialSpecs/ad/ad.actions";
import { AdSizeSection, AdType, DigitalBriefSection, JobDetailsSection, UserDataSection } from "./components/sections";
import { closeDialogIfInIframe } from "store/features/briefForm/utils";

export const BriefingForm = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const briefForm = useSelector(getBriefForm);
    const isSubmitted = useSelector(hasFormBeenSubmitted);
    const adInfo = useSelector(getAd);
    const { isProcessing: isAdProcessing, hasError: adHasError } = useSelector(getAdStatus);
    const { isProcessing, hasError } = useSelector(getBriefFormProcessingStatus);
    const [adOption, setAdOption] = useState<AdSizeType>(briefForm.adSize.type);
    const { materialSpecificationId } = useParams<{ materialSpecificationId: string }>();
    const { handleSubmit, control, errors, getValues, setValue, reset } = useForm<BriefingFormModel>({
        resolver: yupResolver(briefingFormUpdateSchema),
        defaultValues: briefForm,
    });
    const [selectedAdSizesIds, setSelectedAdSizesIds] = useState<string[]>(briefForm.adSize.sizes);
    useProcessingStatus(isProcessing || isAdProcessing, hasError || adHasError);
    useEffect(() => {
        reset(briefForm);
        setAdOption(briefForm.adSize.type);
        setSelectedAdSizesIds(briefForm.adSize.sizes);
    }, [reset, briefForm]);

    useEffect(() => {
        dispatch(fetchBriefForm.request({ materialSpecificationId }));
        dispatch(fetchAd.request(materialSpecificationId));
    }, [dispatch, materialSpecificationId]);

    useEffect(() => {
        setValue("digitalBrief.ioNumber", adInfo.orderNumber);
        if (briefForm.salesRep?.name === "") {
            setValue("salesRep", adInfo.salesRep);
        }
        //eslint-disable-next-line
    }, [setValue, adInfo]);

    useEffect(() => {
        if (isSubmitted) {
            closeDialogIfInIframe();
        }
    }, [isSubmitted]);

    const handleTruSkinSelection = (selectedIds: string[], truSkinIds: string[]): string[] => {
        const truSkinSelected = selectedIds.find((id) => id.includes(TRU_SKIN));
        if (truSkinSelected) {
            return [...selectedIds.filter((id) => !id.includes(TRU_SKIN))];
        }

        return [...selectedIds, ...truSkinIds];
    };

    const handleOtherOptionsSelection = (selectedIds: string[], clickedId: string): string[] => {
        if (selectedIds.includes(clickedId)) {
            return [...selectedIds.filter((id) => id !== clickedId)];
        }

        return [...selectedIds, clickedId];
    };

    // eslint-disable-next-line
    const onAdSizeSelect = (onChange: (...event: any[]) => void, clickedId: string | string[]) => {
        let updatedIds: string[];
        // handle multiple ids for tru_skin
        if (Array.isArray(clickedId)) {
            updatedIds = handleTruSkinSelection(selectedAdSizesIds, clickedId);
        } else {
            updatedIds = handleOtherOptionsSelection(selectedAdSizesIds, clickedId);
        }
        setSelectedAdSizesIds(updatedIds);

        onChange(updatedIds);
    };

    const onAdOptionChange = (option: AdSizeType) => {
        setAdOption(option);
        setSelectedAdSizesIds([]);
        setValue("adSize.sizes", []);
    };

    const onResetOtherDescriptionValue = () => setValue("adSize.otherDescription", "");

    const onSubmit = (values: BriefingFormModel) => {
        dispatch(
            submitBriefForm.request({
                ...values,
                materialSpecificationId: parseInt(materialSpecificationId),
            })
        );
    };

    const onUploadAsset = (files: File[], index: number) => {
        dispatch(
            uploadBriefFormAsset.request({
                ...createBriefingFormUpdateModel(getValues),
                materialSpecificationId: parseInt(materialSpecificationId),
                file: files[0],
                modifiedAssetIndex: index,
            })
        );
    };

    const onAdditionalAssetsUpload = (files: File[]) => {
        dispatch(
            uploadBriefFormAdditionalAssets.request({
                ...createBriefingFormUpdateModel(getValues),
                files,
                materialSpecificationId: parseInt(materialSpecificationId),
            })
        );
    };

    const onDeleteBriefFormAsset = (id: number, removeFromArray?: boolean) => {
        dispatch(
            removeBriefFormAsset.request({
                ...createBriefingFormUpdateModel(getValues),
                materialSpecificationId: parseInt(materialSpecificationId),
                assetId: id,
                removeFromArray,
            })
        );
    };

    return (
        <Container>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stepper orientation="vertical">
                    <Step active>
                        <StepLabel>{t("briefingForm.label.userData")}</StepLabel>
                        <StepContent>
                            <Container maxWidth="sm">
                                <UserDataSection control={control} errors={errors} />
                            </Container>
                        </StepContent>
                    </Step>
                    <Step active>
                        <StepLabel>{t("briefingForm.label.jobDetails")}</StepLabel>
                        <StepContent>
                            <Container maxWidth="sm">
                                <JobDetailsSection name={adInfo.customer.name} dueDate={adInfo.dueDate} />
                            </Container>
                        </StepContent>
                    </Step>
                    <Step active>
                        <StepLabel>{t("briefingForm.label.adSize")}</StepLabel>
                        <StepContent>
                            <AdSizeSection
                                control={control}
                                errors={errors}
                                selectedOption={adOption}
                                onAdOptionChange={onAdOptionChange}
                                selectedIds={selectedAdSizesIds}
                                onAdSizeSelect={onAdSizeSelect}
                                onResetOtherDescriptionValue={onResetOtherDescriptionValue}
                            />
                        </StepContent>
                    </Step>
                    <Step active>
                        <StepLabel>{t("briefingForm.label.adTypeDesign")}</StepLabel>
                        <StepContent>
                            <Container maxWidth="md">
                                <AdType control={control} errors={errors} />
                            </Container>
                        </StepContent>
                    </Step>

                    <Step active>
                        <StepLabel>{t("briefingForm.label.digitalBrief")}</StepLabel>
                        <StepContent>
                            <Container maxWidth="md">
                                <DigitalBriefSection
                                    control={control}
                                    onDeleteBriefFormAsset={onDeleteBriefFormAsset}
                                    errors={errors}
                                    onUploadAsset={onUploadAsset}
                                    onAdditionalAssetsUpload={onAdditionalAssetsUpload}
                                />
                            </Container>
                        </StepContent>
                    </Step>
                </Stepper>
                <Box visibility={!isEmpty(errors) ? "visible" : "hidden"} textAlign="right">
                    <Typography color="error">{t("briefingForm.errors.fixProblems")}</Typography>
                </Box>

                <Box display="flex" justifyContent="flex-end" mb={4}>
                    <Button variant="contained" color="primary" type="submit">
                        {t("buttons.label.submit")}
                    </Button>
                </Box>
            </form>
        </Container>
    );
};
